import { c as _c } from "react/compiler-runtime";
import { SECTION_CONTAINER_WIDTHS, type SectionContainerWidth } from "@/components/atoms/section-constants";
import { SectionBackground } from "../modules/Section/SectionBackground";
import { SectionContent } from "../modules/Section/SectionContent";
import { BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { isDarkColor } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { ContentImage, ContentVideo } from "@/content/types";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
interface PortablePullQuoteProps {
  value: {
    quote: string;
    author: string;
    company?: string;
    style?: {
      backgroundColor?: string;
      backgroundImage?: ContentImage;
      backgroundVideo?: ContentVideo;
      backgroundPattern?: string;
      patternOpacity?: number;
      textColor?: string;
      paddingTop?: string;
      paddingBottom?: string;
      containerWidth?: string;
      customClasses?: string;
    };
  };
}
const paddingMapping: Record<string, string> = {
  "pt-0": "pt-0",
  "pt-8": "pt-8",
  "pt-16": "pt-16",
  "pt-24": "pt-24",
  "pt-32": "pt-32",
  "pb-0": "pb-0",
  "pb-8": "pb-8",
  "pb-16": "pb-16",
  "pb-24": "pb-24",
  "pb-32": "pb-32"
};

/**
 * PortablePullQuote Component
 *
 * An enhanced, high-impact pull quote with premium decorative elements.
 */
const PortablePullQuote = t0 => {
  const $ = _c(52);
  const {
    value
  } = t0;
  const {
    quote,
    author,
    company,
    style
  } = value;
  const bgColor = style?.backgroundColor || "off-white";
  const paddingTop = style?.paddingTop || "pt-16";
  const paddingBottom = style?.paddingBottom || "pb-16";
  const containerWidthValue = style?.containerWidth || "max-w-4xl";
  const ptClass = paddingMapping[paddingTop] || "pt-16";
  const pbClass = paddingMapping[paddingBottom] || "pb-16";
  const containerSize = (containerWidthValue in SECTION_CONTAINER_WIDTHS ? containerWidthValue : "max-w-4xl") as SectionContainerWidth;
  let t1;
  if ($[0] !== bgColor) {
    t1 = isDarkColor(bgColor);
    $[0] = bgColor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDark = t1;
  const t2 = style?.customClasses;
  let t3;
  if ($[2] !== pbClass || $[3] !== ptClass || $[4] !== t2) {
    t3 = cn("not-prose pull-quote my-24 rounded-sm overflow-hidden border border-gray-100/50 shadow-xl group", ptClass, pbClass, t2);
    $[2] = pbClass;
    $[3] = ptClass;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = style?.backgroundImage;
  const t5 = style?.backgroundVideo;
  const t6 = style?.backgroundPattern as BackgroundPatternType;
  const t7 = style?.patternOpacity;
  const t8 = isDark ? "bg-primary/30" : "bg-primary/10";
  let t9;
  if ($[6] !== t8) {
    t9 = cn("absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[var(--aq-glow-size)] h-[var(--aq-glow-size)] opacity-20 blur-massive rounded-full pointer-events-none transition-colors duration-1000", t8);
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t9) {
    t10 = <Box className={t9} />;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = isDark ? "text-primary/20" : "text-primary/20";
  let t12;
  if ($[10] !== t11) {
    t12 = cn("block text-8xl md:text-9xl font-serif leading-none mb-2 select-none", t11);
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== t12) {
    t13 = <Text className={t12}>“</Text>;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const t14 = isDark ? "text-white" : "text-foreground";
  let t15;
  if ($[14] !== t14) {
    t15 = cn("text-2xl font-medium leading-pull-quote mb-12 italic tracking-tight", t14);
    $[14] = t14;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== quote || $[17] !== t15) {
    t16 = <blockquote className={t15}>{quote}</blockquote>;
    $[16] = quote;
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  const t17 = isDark ? "text-white" : "text-foreground";
  let t18;
  if ($[19] !== t17) {
    t18 = cn("absolute -bottom-12 -right-4 text-9xl font-serif leading-none opacity-5 select-none pointer-events-none transition-transform duration-1000 group-hover:translate-x-4", t17);
    $[19] = t17;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== t18) {
    t19 = <Box className={t18}>”</Box>;
    $[21] = t18;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Box className="w-12 h-0.5 bg-primary/60" />;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  const t21 = isDark ? "text-white" : "text-foreground";
  let t22;
  if ($[24] !== t21) {
    t22 = cn("not-italic text-xl font-bold block tracking-wide", t21);
    $[24] = t21;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] !== author || $[27] !== t22) {
    t23 = <cite className={t22}>{author}</cite>;
    $[26] = author;
    $[27] = t22;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  let t24;
  if ($[29] !== company) {
    t24 = company && <Text className="text-xs font-bold text-primary uppercase tracking-ultra-wide opacity-80">{company}</Text>;
    $[29] = company;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] !== t23 || $[32] !== t24) {
    t25 = <Box className="flex flex-col items-center gap-4">{t20}<Box className="space-y-1">{t23}{t24}</Box></Box>;
    $[31] = t23;
    $[32] = t24;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== t13 || $[35] !== t16 || $[36] !== t19 || $[37] !== t25) {
    t26 = <Box className="relative z-10 mx-auto text-center px-4">{t13}{t16}{t19}{t25}</Box>;
    $[34] = t13;
    $[35] = t16;
    $[36] = t19;
    $[37] = t25;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] !== containerSize || $[40] !== t10 || $[41] !== t26) {
    t27 = <SectionContent size={containerSize}>{t10}{t26}</SectionContent>;
    $[39] = containerSize;
    $[40] = t10;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== bgColor || $[44] !== isDark || $[45] !== t27 || $[46] !== t3 || $[47] !== t4 || $[48] !== t5 || $[49] !== t6 || $[50] !== t7) {
    t28 = <SectionBackground as="section" aria-label="Pull quote" className={t3} backgroundColor={bgColor} backgroundImage={t4} backgroundVideo={t5} backgroundPattern={t6} patternOpacity={t7} isDark={isDark}>{t27}</SectionBackground>;
    $[43] = bgColor;
    $[44] = isDark;
    $[45] = t27;
    $[46] = t3;
    $[47] = t4;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  return t28;
};
export default PortablePullQuote;